<template>
  <div class="dashboard-layout d-flex flex-column min-vh-100">
    <!-- Navbar -->
    <NavbarView @toggle-sidebar="toggleSidebar" :sidebarOpen="sidebarOpen" />

    <!-- Sidebar -->
    <SidebarView
      :isOpen="sidebarOpen"
      @close-sidebar="closeSidebar"
      @select-page="selectPage"
    />

    <!-- Overlay (Mobile) -->
    <div
      v-if="sidebarOpen && !isDesktop"
      class="overlay"
      @click="closeSidebar"
    ></div>

    <!-- Main Content -->
    <main
      :class="[
        'content flex-grow-1',
        { 'content-shift': sidebarOpen && isDesktop },
      ]"
    >
      <!-- Conditional rendering based on selectedPage -->
      <section v-if="selectedPage === 'home'">
        <AdminHome />
        <!-- Add video player or video list -->
      </section>

      <section v-if="selectedPage === 'mytopics'">
        <MyTopics />
        <!-- Add video player or video list -->
      </section>

      <section v-if="selectedPage === 'enrollments'">
        <EnrollmentsView />
        <!-- Add profile form/details -->
      </section>

      <section v-if="selectedPage === 'payments'">
        <PaymentView />
        <!-- Add profile form/details -->
      </section>

      <section v-if="selectedPage === 'students'">
        <StudentsView />
        <!-- Add profile form/details -->
      </section>

      <section v-if="selectedPage === 'videoLessons'">
        <VideoLessons />
        <!-- Add profile form/details -->
      </section>

      <section v-if="selectedPage === 'settings'">
        <SettingsView />
        <!-- Add profile form/details -->
      </section>

      <!-- Add more sections as needed -->
    </main>

    <!-- Footer -->
    <FooterView :sidebarOpen="sidebarOpen" />
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from "vue";
import NavbarView from "@/components/StudentDashboard/NavbarView.vue";
import SidebarView from "@/components/StudentDashboard/SidebarView.vue";
import FooterView from "@/components/StudentDashboard/FooterView.vue";
import AdminHome from "./DashboardContent/AdminHome.vue";
import EnrollmentsView from "./DashboardContent/EnrollmentsView.vue";
import PaymentView from "./DashboardContent/PaymentView.vue";
import StudentsView from "./DashboardContent/StudentsView.vue";
import VideoLessons from "./DashboardContent/VideoLessons.vue";
import MyTopics from "./DashboardContent/MyTopics.vue";
import SettingsView from "./DashboardContent/SettingsView.vue";

const sidebarOpen = ref(false);
const isDesktop = ref(window.innerWidth >= 992);

// Track selected page
const selectedPage = ref("home"); // default page

// Toggle sidebar
const toggleSidebar = () => {
  sidebarOpen.value = !sidebarOpen.value;
};
const closeSidebar = () => {
  sidebarOpen.value = false;
};

// Handle page selection from sidebar
const selectPage = (page) => {
  selectedPage.value = page;
  // close sidebar on mobile
  if (!isDesktop.value) sidebarOpen.value = false;
};

// Handle window resize
const handleResize = () => {
  isDesktop.value = window.innerWidth >= 992;
  if (!isDesktop.value) sidebarOpen.value = false;
};

onMounted(() => window.addEventListener("resize", handleResize));
onUnmounted(() => window.removeEventListener("resize", handleResize));
</script>

<style scoped>
.dashboard-layout {
  background: #0d0015;
  color: white;
  overflow-x: hidden;
}

/* ================= CONTENT ================= */

.content {
  transition: all 0.3s ease;
  margin-left: 0;
  padding: 20px;
  min-height: 100vh;
}

/* Desktop sidebar shift */
@media (min-width: 992px) {
  .content-shift {
    margin-left: 250px;
  }
}

/* Mobile adjustments */
@media (max-width: 991.98px) {
  .content {
    padding: 15px;
  }

  section {
    padding-top: 80px; /* space for fixed navbar */
  }
}

/* ================= OVERLAY ================= */

.overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(2px);
  z-index: 1039;
}

/* ================= FOOTER ================= */

.axion-footer {
  background: #000;
  border-top: 1px solid #ff00cc;
  color: #aaa;
  padding: 15px;
  text-align: center;
}

/* ================= SECTIONS ================= */

section {
  padding-top: 100px; /* space for fixed navbar */
}

/* Make tables responsive automatically */
.table {
  width: 100%;
}

.table-responsive {
  overflow-x: auto;
}

/* Make cards stack nicely on mobile */
@media (max-width: 768px) {
  .row > div {
    margin-bottom: 15px;
  }
}
</style>
